/**
 * 탭 틀(/workspace) 안의 GROUPCREW 화면 (spec docs/superpowers/specs/2026-09-29-desktop-workspace-tabs-design.md §5.3 · §5.6).
 *
 * main.js 의 **첫 import** 다 — 모듈 평가가 import 순서라 다른 어떤 모듈보다 먼저 history.pushState 를 잡는다.
 * 틀 안에서는 pushState 가 칸을 쌓지 않고(replaceState) 틀에 주소를 알린다: 부모와 iframe 들이 뒤로가기
 * 기록 하나를 같이 쓰므로(joint session history), 그대로 두면 뒤로가기가 다른 탭을 되감는다. 탭마다
 * 기록은 틀이 가진다(workspace-logic.js TabHistory).
 *
 * 틀 밖에서는 데스크톱 설치 앱 창의 자동 감싸기(§6.2)만 한다.
 */
import {
    FRAME_PREFIX, CLOSE_SENTINEL, classifyLink, shellKey, helpKey, shouldDrawShell, wrapTarget, phoneUA, wrapLooping,
    isMacPlatform, isEditableTarget,
} from './workspace-logic.js';
import { isPhone } from './phone.js';
import { navigate, spaTarget, setNavigateInterceptor } from './calendar-spa.js';
import { appConfirm } from './confirm-dialog.js';

function detect() {
    try {
        return window.parent !== window
            && window.parent.location.origin === window.location.origin
            && String(window.name || '').startsWith(FRAME_PREFIX);
    } catch (_) {
        return false;
    }
}

/** 이 문서가 탭 틀 안의 탭인가. */
export const IN_WORKSPACE = detect();

function standalone() {
    const mm = (q) => !!(window.matchMedia && window.matchMedia(q).matches);
    return mm('(display-mode: standalone)') || mm('(display-mode: window-controls-overlay)') || navigator.standalone === true;
}

const WRAP_MARK = 'ws.wrap';

/** 틀 밖의 데스크톱 설치 앱 창이면 틀로 옮긴다 (§6.2). 옮기는 중이면 true.
 *  서버가 /workspace 를 이 화면으로 되돌려 보내는 경우(폰 User-Agent · 보안 게이트 · 모르는 경우)에
 *  돌지 않도록, 같은 경로를 방금 감쌌으면 감싸지 않는다. */
export const WRAPPING = (() => {
    if (IN_WORKSPACE || window.top !== window) return false;
    if (!shouldDrawShell({ standalone: standalone(), phone: isPhone() }) || phoneUA(navigator.userAgent)) return false;
    const loc = window.location;
    const to = wrapTarget(loc.pathname, loc.search, loc.hash);
    if (!to) return false;
    const path = loc.pathname + loc.search;
    const now = Date.now();
    try {
        if (wrapLooping(JSON.parse(sessionStorage.getItem(WRAP_MARK) || 'null'), path, now)) return false;
        sessionStorage.setItem(WRAP_MARK, JSON.stringify({ path, t: now }));
    } catch (_) { /* 저장소가 없으면 한 번은 감싼다 — 서버가 되돌리면 그 뒤로는 같은 경로라도 */ }
    loc.replace(to);
    return true;
})();

function shell() {
    try { return window.parent.__workspace || null; } catch (_) { return null; }
}

function post(type, data) {
    try { window.parent.postMessage(Object.assign({ type }, data || {}), window.location.origin); } catch (_) { /* 틀이 사라졌다 */ }
}

/** 탭 틀 안이면 틀에 메시지를 보내고 true, 아니면 false (apps.js 의 옛 딥링크 /apps?open= 이 쓴다). */
export function postToShell(type, data) {
    if (!IN_WORKSPACE) return false;
    post(type, data);
    return true;
}

function report(how) {
    post('ws:location', { url: window.location.href, title: document.title, how });
}

let skipUnload = false;
let skipTimer = 0;
let wired = false;
const MAC = isMacPlatform();

if (IN_WORKSPACE) {
    document.documentElement.dataset.workspaceFrame = '1';
    const h = window.history;
    const nativeReplace = h.replaceState;
    h.pushState = function (state, title, url) {
        const out = nativeReplace.call(h, state, title, url);
        report('push');
        return out;
    };
    h.replaceState = function (state, title, url) {
        const out = nativeReplace.call(h, state, title, url);
        report('replace');
        return out;
    };
    // 틀이 이미 앱 대화창으로 물어본 이동이면 페이지의 beforeunload 가 또 묻지 않게 — 이 리스너가 첫 번째다.
    window.addEventListener('beforeunload', (e) => { if (skipUnload) e.stopImmediatePropagation(); });
    // 틀의 명령 · 마우스 뒤로/앞으로는 모듈을 읽자마자 받는다 — 페이지 setup 이 중간에 멈춰도, 새 문서가 뜨는 중에도
    // 탭이 대답하고, 마우스 버튼이 창 전체의 기록을 되감지 않는다.
    window.addEventListener('message', onMessage);
    window.addEventListener('mousedown', onMouse, true);
    window.addEventListener('mouseup', onMouse, true);
    // 단축키 · 클릭은 main.js 의 setup 이 끝난 뒤(setupWorkspaceFrame) 마지막으로 등록한 쪽이 받는다 — 그 전까지만 여기서.
    window.addEventListener('keydown', (e) => { if (!wired) onKey(e); });
    window.addEventListener('click', (e) => { if (!wired) onClick(e); });
    window.addEventListener('auxclick', (e) => { if (!wired) onClick(e); });
    // setup 이 멈춰 setupWorkspaceFrame 에 닿지 못해도 첫 보고는 간다.
    window.addEventListener('load', () => { if (!wired) report('load'); });
}

function labels() {
    const s = shell();
    return (s && s.labels) || {};
}

function makeUnloadEvent() {
    try {
        const ev = document.createEvent('BeforeUnloadEvent');
        ev.initEvent('beforeunload', false, true);
        return ev;
    } catch (_) {
        return new Event('beforeunload', { cancelable: true });
    }
}

// 이 문서를 내리면 페이지의 beforeunload 가 막는가 (저장하지 않은 입력).
function unloadGuarded() {
    skipUnload = false;
    const ev = makeUnloadEvent();
    window.dispatchEvent(ev);
    return ev.defaultPrevented || (typeof ev.returnValue === 'string' && ev.returnValue !== '');
}

// 내리기 전에 앱 대화창으로 한 번 묻고, 떠나기로 하면 브라우저가 또 묻지 않게 한다.
function leave(go, stay, message, onAsk) {
    if (!unloadGuarded()) { go(); return; }
    if (onAsk) onAsk();
    appConfirm({ message: message || '', danger: true }).then((ok) => {
        if (!ok) { if (stay) stay(); return; }
        skipUnload = true;
        // 이동이 문서를 내리지 않았으면(내려받기 · 204 · 다른 질문이 이김) 다음 진짜 떠나기는 다시 묻는다.
        clearTimeout(skipTimer);
        skipTimer = setTimeout(() => { skipUnload = false; }, 4000);
        go();
    });
}

function spaReady(href) {
    return document.documentElement.dataset.spaReady === '1' && spaTarget(href);
}

function shellNavigate(raw) {
    let u;
    try { u = new URL(raw, window.location.href); } catch (_) { return; }
    if (u.origin !== window.location.origin) return;
    const blocked = () => post('ws:nav-blocked', { url: u.href });
    const ev = new CustomEvent('calendar:before-navigate', { cancelable: true, detail: { href: u.href, path: u.pathname, workspace: true } });
    if (!document.dispatchEvent(ev)) { blocked(); return; }
    if (spaReady(u.href)) { navigate(u.href, { replace: true }); return; }
    leave(() => window.location.replace(u.href), blocked, labels().unsaved_leave, () => post('ws:asking'));
}

let closing = '';

function canClose(token) {
    post('ws:close-ack', { token });
    closing = token;
    const answer = (ok) => {
        if (closing !== token) return;
        closing = '';
        setNavigateInterceptor(null);
        post('ws:close-answer', { token, ok });
    };
    setNavigateInterceptor((href) => {
        let u;
        try { u = new URL(href, window.location.href); } catch (_) { return false; }
        if (u.origin !== window.location.origin || u.pathname !== CLOSE_SENTINEL) return false;
        answer(true);
        return true;
    });
    const ev = new CustomEvent('calendar:before-navigate', { cancelable: true, detail: { href: window.location.origin + CLOSE_SENTINEL, path: CLOSE_SENTINEL, close: true } });
    if (!document.dispatchEvent(ev)) {
        // 가드가 묻고 있다 — 떠나기로 하면 navigate(CLOSE_SENTINEL, {force}) 로 온다.
        post('ws:close-asking', { token });
        return;
    }
    leave(() => answer(true), () => answer(false), labels().unsaved_close, () => post('ws:close-asking', { token }));
}

// 탭 안의 문서 이동은 창 기록에 칸을 만들지 않는다(replace) — 기록은 틀이 가진다 (§5.3).
function goHere(href, spa) {
    if (spa && spaReady(href)) navigate(href);
    else window.location.replace(href);
}

// 클릭 · 키는 window 의 **버블** 단계에서, 페이지가 이미 처리한 것(defaultPrevented)은 건드리지 않고 받는다 —
// 캡처 단계에서 멈추면 링크를 가로채는 페이지 모듈(결재함 모달 · 업무 패널 · SPA 라우터)과
// 모달의 Alt+←/→ 가 탭 안에서 모두 죽는다.
function onClick(e) {
    if (e.defaultPrevented) return;
    if (e.type === 'click' && e.button !== 0) return;
    if (e.type === 'auxclick' && e.button !== 1) return;
    // 새 탭 화면(/apps)의 「단축키 보기」 한 줄 — 틀의 도움말을 연다
    if (e.type === 'click' && e.target && e.target.closest && e.target.closest('[data-ws-help]')) {
        e.preventDefault();
        post('ws:key', { action: 'help' });
        return;
    }
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    const raw = a.getAttribute('href') || '';
    if (!raw || raw.startsWith('#') || /^javascript:/i.test(raw)) return;
    let url;
    try { url = new URL(a.href, window.location.href); } catch (_) { return; }
    if (url.origin === window.location.origin && url.pathname === '/logout') { e.preventDefault(); post('ws:logout'); return; }
    if (a.dataset.wsApp) { e.preventDefault(); post('ws:app', { id: a.dataset.wsApp }); return; }
    const s = shell();
    const r = classifyLink({
        url, origin: window.location.origin, target: a.getAttribute('target'), button: e.button,
        ctrl: e.ctrlKey, meta: e.metaKey, shift: e.shiftKey, download: a.hasAttribute('download'),
        services: s ? s.services : [],
    });
    switch (r.kind) {
    case 'block': e.preventDefault(); return;
    case 'current': e.preventDefault(); goHere(url.href, true); return;
    case 'tab':
    case 'service': e.preventDefault(); post('ws:open', { url: url.href, mode: r.mode }); return;
    case 'external': e.preventDefault(); window.open(url.href, '_blank', 'noopener'); return;
    default:
        // 아무도 받지 않은 같은 사이트 링크(SPA 가 아닌 화면) — 문서 이동을 replace 로.
        if (e.type === 'click' && url.origin === window.location.origin && !a.hasAttribute('download')) {
            e.preventDefault();
            goHere(url.href, false);
        }
    }
}

// 대리 보기 시작(/admin/users/:id/impersonate) · 끝(/impersonate/stop).
const SESSION_CHANGE = /^\/(?:impersonate(?:\/|$)|admin\/users\/[^/?#]+\/impersonate(?:[?#]|$))/;

function onSubmit(e) {
    const f = e.target;
    if (!(f instanceof HTMLFormElement)) return;
    const actionOf = () => (e.submitter && e.submitter.getAttribute('formaction')) || f.getAttribute('action') || '';
    if (actionOf() === '/logout') { e.preventDefault(); post('ws:logout'); return; }
    // 확인 창이 이 제출을 멈췄는지, 다른 리스너가 action 을 맞췄는지(대행 로그인 폼은 하나를 행마다 겨눈다)는
    // 모든 리스너가 돈 뒤에야 안다 — 멈췄으면 알리지 않는다 (확인하면 requestSubmit 으로 다시 온다).
    setTimeout(() => { if (!e.defaultPrevented && SESSION_CHANGE.test(actionOf())) post('ws:session-change'); }, 0);
}

function onKey(e) {
    if (e.defaultPrevented) return;
    const editable = isEditableTarget(e.target);
    let action = shellKey(e, { mac: MAC, editable });
    // ? 는 틀의 단축키 도움말 — 페이지가 먼저 받았으면(영업의 단축키 안내 등 — defaultPrevented) 그 페이지의 것이고,
    // 페이지에 대화창이 떠 있으면 그 위에 띄우지 않는다.
    if (!action && helpKey(e, editable) && !document.querySelector('.modal-overlay.show, .confirm-dialog-overlay.show')) action = 'help';
    if (!action) return;
    e.preventDefault();
    post('ws:key', { action });
}

function onMouse(e) {
    if (e.button !== 3 && e.button !== 4) return;
    e.preventDefault();
    if (e.type === 'mouseup') post('ws:key', { action: e.button === 3 ? 'back' : 'forward' });
}

function onMessage(e) {
    if (e.source !== window.parent || e.origin !== window.location.origin) return;
    const d = e.data;
    if (!d || typeof d !== 'object') return;
    if (d.type === 'ws:navigate' && typeof d.url === 'string') shellNavigate(d.url);
    else if (d.type === 'ws:reload') leave(() => window.location.reload(), () => post('ws:nav-blocked', { url: window.location.href }), labels().unsaved_leave, () => post('ws:asking'));
    else if (d.type === 'ws:can-close' && d.token) canClose(String(d.token));
}

function wrapOpen() {
    const nativeOpen = window.open;
    window.open = function (url, target, features) {
        const popup = typeof features === 'string' && /(?:^|,)\s*(?:popup|width|height|left|top)\b/i.test(features);
        const t = String(target || '').toLowerCase();
        if (popup || !url || t === '_self' || t === '_top' || t === '_parent') return nativeOpen.apply(window, arguments);
        let u;
        try { u = new URL(String(url), window.location.href); } catch (_) { return nativeOpen.apply(window, arguments); }
        const s = shell();
        const r = classifyLink({ url: u, origin: window.location.origin, target: '_blank', services: s ? s.services : [] });
        if (r.kind === 'tab' || r.kind === 'service') { post('ws:open', { url: u.href, mode: 'foreground' }); return null; }
        return nativeOpen.apply(window, arguments);
    };
}

function watchBusy() {
    const root = document.documentElement;
    let on = root.classList.contains('spa-loading');
    new MutationObserver(() => {
        const now = root.classList.contains('spa-loading');
        if (now !== on) { on = now; post('ws:busy', { on }); }
    }).observe(root, { attributes: true, attributeFilter: ['class'] });
}

function watchTitle() {
    let last = document.title;
    const head = document.head || document.documentElement;
    new MutationObserver(() => {
        if (document.title !== last) { last = document.title; post('ws:title', { title: last }); }
    }).observe(head, { childList: true, characterData: true, subtree: true });
}

/** main.js 가 다른 모든 setup 뒤에 부른다 — 첫 보고가 그 뒤의 주소(_from 지우기 등)를 싣도록, 그리고 단축키 · 클릭을
 *  페이지 모듈들보다 늦게 등록해 그것들이 먼저 쓰게(defaultPrevented) 하도록. */
export function setupWorkspaceFrame() {
    if (!IN_WORKSPACE || wired) return;
    wired = true;
    window.addEventListener('click', onClick);
    window.addEventListener('auxclick', onClick);
    window.addEventListener('keydown', onKey);
    document.addEventListener('submit', onSubmit, true);
    window.addEventListener('hashchange', () => report('push'));
    window.addEventListener('pagehide', () => post('ws:busy', { on: true }));
    wrapOpen();
    watchBusy();
    watchTitle();
    report('load');
}
